import { batch, signal } from '@preact/signals';
import type { SandboxMessage, ScrollPosition } from './sandbox';

export type OutputItem =
  | { kind: 'stdout' | 'stderr' | 'info' | 'log' | 'warn' | 'error' | 'result'; text: string }
  | { kind: 'figure'; src: string };

export type RunStatus = 'idle' | 'loading' | 'running';

export const outputItems = signal<OutputItem[]>([]);
export const runStatus = signal<RunStatus>('idle');
/** Human-readable progress such as "Loading Python runtime". */
export const runLabel = signal<string>('');
/** srcdoc for the sandboxed preview frame, or null when nothing is being previewed. */
export const previewDoc = signal<string | null>(null);
export const previewKind = signal<'html' | 'javascript' | null>(null);
/** The code the preview was built from, so a live preview re-renders only when the code really changed. */
export const previewSource = signal<string | null>(null);
/** Where the reader had scrolled the preview, restored when a live update re-renders it. */
export const previewScroll = signal<ScrollPosition | null>(null);
/**
 * Bumped on every sandboxed run. The preview frame is keyed on it, so running unchanged code
 * still gets a fresh frame; keying on the document alone would skip identical re-runs.
 */
export const runSequence = signal<number>(0);
/** True once the current run raised an error (an exception, not merely text on stderr). */
export const runFailed = signal<boolean>(false);
/** Counts finished runs, so other parts of the app can react when one ends. */
export const runCompletions = signal<number>(0);

export function markRunFinished(): void {
  runCompletions.value += 1;
}

const MAX_ITEMS = 2000;

export function appendOutput(item: OutputItem): void {
  const items = outputItems.value;
  const last = items[items.length - 1];
  // Merge consecutive stream chunks of the same kind so long prints do not create thousands of rows.
  if (last && item.kind !== 'figure' && last.kind === item.kind && item.kind !== 'result') {
    const merged: OutputItem = { kind: last.kind, text: last.text + item.text };
    outputItems.value = [...items.slice(0, -1), merged];
    return;
  }
  const next = [...items, item];
  outputItems.value = next.length > MAX_ITEMS ? next.slice(next.length - MAX_ITEMS) : next;
}

export function clearOutput(): void {
  outputItems.value = [];
  runFailed.value = false;
}

/** Forget the run and the preview entirely, for example when the user moves to another card. */
export function resetOutput(): void {
  batch(() => {
    clearOutput();
    previewDoc.value = null;
    previewKind.value = null;
    previewSource.value = null;
    previewScroll.value = null;
  });
}

/** Handle a message from a sandboxed frame, wherever the frame lives (the output panel or the preview window). */
export function receiveSandboxMessage(message: SandboxMessage): void {
  if (message.level === 'done') {
    markRunFinished();
    return;
  }
  if (message.level === 'scroll') {
    const [x, y] = message.text.split(',').map(Number);
    if (Number.isFinite(x) && Number.isFinite(y)) previewScroll.value = { x: x!, y: y! };
    return;
  }
  if (message.level === 'error') markRunFailed();
  appendOutput({ kind: message.level, text: `${message.text}\n` });
}

export function markRunFailed(): void {
  runFailed.value = true;
}

export function hasErrorOutput(): boolean {
  return runFailed.value;
}

/** Text of the run for the "Fix with AI" action: everything, with errors kept in order. */
export function outputText(): string {
  return outputItems.value
    .map((item) => {
      if (item.kind === 'figure') return '[figure]';
      const prefix = item.kind === 'stderr' || item.kind === 'error' ? '[error] ' : item.kind === 'warn' ? '[warn] ' : '';
      return prefix + item.text;
    })
    .join('\n');
}
